import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LoadingGroup = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('loading_groups');
  const [viewItem, setViewItem] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadLoadingGroups();
  }, []);

  const loadLoadingGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loading-groups`);
      setLoadingGroups(response.data || []);
    } catch (error) {
      console.error('Error loading loading groups:', error);
      toast.error('Failed to load loading groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    setEditingGroup(group);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      loading_group_id: (formData.get('loading_group_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/loading-groups/${editingGroup.loading_group_id}`, {
          description: data.description
        });
        toast.success('Loading Group updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/loading-groups`, data);
        toast.success('Loading Group created successfully');
      }
      setIsDialogOpen(false);
      loadLoadingGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save loading group');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Groups</h1>
            <p className="text-sm text-gray-500 mt-1">Manage loading group classifications</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Loading Groups</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Loading Group
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Loading Group ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No loading groups found. Click &quot;Add Loading Group&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    loadingGroups.map((group) => (
                      <tr key={group.loading_group_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{group.loading_group_id}</td>
                        <td className="py-3 px-4 text-sm">{group.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="loading_groups" onView={() => setViewItem(group)} onEdit={() => handleOpenDialog(group)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Edit Loading Group' : 'Add Loading Group'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Loading Group ID *</Label>
                  <Input
                    name="loading_group_id"
                    defaultValue={editingGroup?.loading_group_id || ''}
                    placeholder="e.g., BULK, PALLET, CONTAINER"
                    maxLength={4}
                    minLength={4}
                    pattern="[A-Za-z0-9]{4}"
                    title="Must be exactly 4 alphanumeric characters"
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
                    }}
                    disabled={!!editingGroup}
                    className="uppercase"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier for the loading group</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingGroup?.description || ''}
                    placeholder="e.g., Bulk Loading"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingGroup ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default LoadingGroup;
